import React, { useEffect } from "react";
import {
    AssessmentsReopenRequestItem,
    Participant,
    ParticipantBuildPlanItem,
    UpdateParticipantBuildPlanStatusRequest,
} from "../../../../../services/types/enterpriseParticipantApiTypes";
import AssessmentListDataGridPro from "./assessmentListDataGrid";
import { AlertColor, Box } from "@mui/material";
import AddAssessmentDrawer from "../../../../sessions/sessionDetail/general/assessmentSetup/addAssessmentDrawer";
import AddDocumentDrawer from "../../../../sessions/sessionDetail/general/assessmentSetup/addDocumentDrawer";
import { AssessmentListItem, DocumentList } from "../../../../../services/types/sessionDetailTypes";
import {
    useGetSessionSetUpAssessmentsByEventKeyQuery,
    useLazyGetAIKeybyDocKeyQuery,
} from "../../../../../services/cclTokenedSessionApi";
import { useGetActiveDocumentsQuery } from "../../../../../services/cclTokenedAssessmentApi";
import { AssetDownloadRequest } from "../../../../../services/types/assetApiTypes";
import { FileDownloadService } from "../../../../../services/fileDownloadService/fileDownloadService";
import {
    useAddBuildPlanItemMutation,
    useDisableParticipantBuildPlanItemMutation,
    useEnableParticipantBuildPlanItemMutation,
    useGetParticipantBuildPlanQuery,
    useReopenAssessmentsMutation,
} from "../../../../../services/cclTokenedEnterpriseParticipantApi";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import {
    AccessEventRegistrationAssessmentDetails,
    AccessEventRegistrationDetails,
} from "../../../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";

interface AssessmentListPanelProps {
    registration: Participant | undefined;
    readOnly?: boolean;
}

const AssessmentListPanel: React.FC<AssessmentListPanelProps> = (props) => {
    const downloadService = new FileDownloadService();

    const [showAddAssessmentDrawer, setShowAddAssessmentDrawer] = React.useState(false);
    const [showAddDocumentDrawer, setShowAddDocumentDrawer] = React.useState(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [localBuildPlanItems, setLocalBuildPlanItems] = React.useState<
        ParticipantBuildPlanItem[]
    >([]);
    const [buttonActionInProgress, setButtonActionInProgress] = React.useState<string>("");

    const [enableBuildPlanItem] = useEnableParticipantBuildPlanItemMutation();
    const [disableBuildPlanItem] = useDisableParticipantBuildPlanItemMutation();
    const [getDocsAiKey] = useLazyGetAIKeybyDocKeyQuery();
    const { data: sessionAssessments } = useGetSessionSetUpAssessmentsByEventKeyQuery(
        props.registration?.esKey.toString() ?? "",
        {
            skip: !props.registration?.esKey,
        },
    );
    const { data: paxBuildPlan, isLoading: paxBuildPlanIsLoading } =
        useGetParticipantBuildPlanQuery(props.registration?.esiKey ?? 0, {
            skip: !props.registration?.esiKey,
        });
    const [addBuildPlanItem] = useAddBuildPlanItemMutation();
    const [reopenAssessments] = useReopenAssessmentsMutation();
    const { data: activeDocuments } = useGetActiveDocumentsQuery();

    const documentOptionsList: DocumentList[] = React.useMemo(
        () =>
            (activeDocuments ?? [])
                .map((d) => ({ documentID: d.documentId, documentName: d.documentName }))
                .sort((a, b) =>
                    a.documentName.localeCompare(b.documentName, undefined, { sensitivity: "base" }),
                ),
        [activeDocuments],
    );

    useEffect(() => {
        if (paxBuildPlan && paxBuildPlan.length > 0) {
            setLocalBuildPlanItems([...paxBuildPlan]);
        } else {
            setLocalBuildPlanItems([]);
        }
    }, [paxBuildPlan]);

    const { logEvent } = useLogAccessEvent();
    const evtData: AccessEventRegistrationDetails = {
        esiKey: props.registration?.esiKey.toString() ?? "",
        imKey: props.registration?.imKey.toString() ?? "",
        email: props.registration?.emailAddress ?? "",
    };

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const handleButtonClick = async (buttonId: string, selectedMasterKeys: number[]) => {
        setButtonActionInProgress(buttonId);
        switch (buttonId) {
            case "addassessment":
                setShowAddAssessmentDrawer(true);
                setButtonActionInProgress("");
                break;
            case "adddocument":
                setShowAddDocumentDrawer(true);
                setButtonActionInProgress("");
                break;
            case "downloaddocument":
                if (selectedMasterKeys.length !== 1) return;
                await viewDocument(selectedMasterKeys[0]);
                setButtonActionInProgress("");
                break;
            case "reopenassessments":
                if (selectedMasterKeys.length <= 0) {
                    setButtonActionInProgress("");
                    return;
                }
                const fakeys = localBuildPlanItems
                    .filter(
                        (f) =>
                            selectedMasterKeys.includes(f.cclMasterKey) && f.formActualKey != null,
                    )
                    .map((f) => f.formActualKey);
                var requestItem: AssessmentsReopenRequestItem = {
                    esiKey: props.registration?.esiKey ?? 0,
                    formActualKeys: fakeys.filter((f): f is number => f != null),
                };
                reopenAssessments({ requestItems: [requestItem] })
                    .unwrap()
                    .then(() => {
                        for (let i: number = 0; i < selectedMasterKeys.length; i++) {
                            const instrument = localBuildPlanItems.find(
                                (b) => b.cclMasterKey === selectedMasterKeys[i],
                            );
                            const reopenEvtData: AccessEventRegistrationAssessmentDetails = {
                                esiKey: props.registration?.esiKey.toString() ?? "",
                                imKey: props.registration?.imKey.toString() ?? "",
                                email: props.registration?.emailAddress ?? "",
                                instrument: instrument?.title ?? selectedMasterKeys[i].toString(),
                            };
                            logEvent("ParticipantAssessmentReopened", reopenEvtData);
                        }
                        ShowSnackbarElement("Assessment(s) Re-Opened", "success");
                        setButtonActionInProgress("");
                    })
                    .catch(() => {
                        ShowSnackbarElement("Re-Open Assessment(s) Failed", "error");
                        setButtonActionInProgress("");
                    });
                break;
        }
    };

    const viewDocument = async (docId: number) => {
        try {
            let aidata = await getDocsAiKey(docId).unwrap();
            if (aidata && aidata?.aI_Key > 0) {
                let doc = documentOptionsList.filter((d) => d.documentID === docId);
                if (doc && doc.length > 0) {
                    let request: AssetDownloadRequest = {
                        fname: doc[0].documentName,
                        aikeys: [aidata.aI_Key],
                        flatzip: false,
                    };
                    try {
                        await downloadService.DownloadAssets(request);
                    } catch (error) {
                        ShowSnackbarElement("Error retrieving document", "error");
                    }
                }
            } else {
                ShowSnackbarElement("Document not found", "error");
            }
        } catch (error) {
            ShowSnackbarElement("Error retrieving document", "error");
        }
    };

    const addItem = async (isDocument: boolean, cclMasterKey: number) => {
        try {
            await addBuildPlanItem({
                esiKey: props.registration?.esiKey ?? 0,
                cclMasterKey: cclMasterKey,
                isDoc: isDocument,
            }).unwrap();
            logEvent("RegistrationSetupSaved", evtData);
            ShowSnackbarElement("Assessments & Documents Updated", "success");
        } catch {
            ShowSnackbarElement("Assessments & Documents Update Failed", "error");
        } finally {
            if (isDocument) {
                setShowAddDocumentDrawer(false);
            } else {
                setShowAddAssessmentDrawer(false);
            }
        }
    };

    const handleEnableItemChange = (bpiaKey: number, newState: boolean) => {
        if (!props.registration) return;

        const updatedBuildPlan = localBuildPlanItems?.map((item) =>
            item.bpiA_Key === bpiaKey ? { ...item, enabled: newState } : item,
        );
        setLocalBuildPlanItems([...updatedBuildPlan]);

        const payload: UpdateParticipantBuildPlanStatusRequest = {
            bpiaKey: bpiaKey,
            esiKey: props.registration.esiKey,
        };

        if (newState) {
            enableBuildPlanItem(payload)
                .unwrap()
                .then(() => {
                    logEvent("RegistrationSetupSaved", evtData);
                    ShowSnackbarElement("Assessments & Documents Updated", "success");
                })
                .catch((error) => {
                    ShowSnackbarElement("Assessments & Documents Update Failed", "error");
                });
        } else {
            disableBuildPlanItem(payload)
                .unwrap()
                .then(() => {
                    logEvent("RegistrationSetupSaved", evtData);
                    ShowSnackbarElement("Assessments & Documents Updated", "success");
                })
                .catch((error) => {
                    ShowSnackbarElement("Assessments & Documents Update Failed", "error");
                });
        }
    };

    return (
        <Box width={1}>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setShowSnackbar(false)}
                />
            ) : null}
            {showAddAssessmentDrawer && props.registration?.esKey != null ? (
                <AddAssessmentDrawer
                    open={true}
                    esKey={props.registration?.esKey}
                    onCancel={() => setShowAddAssessmentDrawer(false)}
                    onAdd={(newItem: AssessmentListItem) => addItem(false, newItem.cclMasterKey)}
                    assessmentOptionsList={sessionAssessments?.assessmentOptionsList ?? []}
                    showSnackbar={ShowSnackbarElement}
                />
            ) : null}
            {showAddDocumentDrawer && props.registration?.esKey != null ? (
                <AddDocumentDrawer
                    open={true}
                    esKey={props.registration?.esKey}
                    onCancel={() => setShowAddDocumentDrawer(false)}
                    onAdd={(newItem: AssessmentListItem) => addItem(true, newItem.cclMasterKey)}
                    onViewDocument={viewDocument}
                    documentOptionsList={documentOptionsList}
                    showSnackbar={ShowSnackbarElement}
                />
            ) : null}
            <AssessmentListDataGridPro
                loading={paxBuildPlanIsLoading}
                assessments={localBuildPlanItems ?? []}
                handleButtonClick={handleButtonClick}
                readOnly={props.readOnly}
                handleItemEnabledChange={handleEnableItemChange}
                actionInProgress={buttonActionInProgress}
            />
        </Box>
    );
};

export default AssessmentListPanel;
